:root {
  --primary-color: #635BFF;
  --primary-hover: #574ce0;
  --background-light: #F7F9FC;
  --card-background: white;
  --text-primary: #2D3748;
  --text-secondary: #718096;
  --text-details: #4A5568;
  --success-color: #48BB78;
  --warning-color: #F59E0B;
  --danger-color: #EF4444;
}

.container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-light);
}

.content {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px 40px;
}

.pageHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-top: 40px;
}

.pageHeader h1 {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
}

.backButton {
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.backButton:hover {
  background-color: var(--primary-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(99, 91, 255, 0.3);
}

/* Date Range Filter Section */
.filterSection {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 30px;
}

.dateRangeSelector {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #E2E8F0;
  color: var(--text-primary);
}

.dateRangeSelect {
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.dateRangeSelect:hover {
  background-color: #F7FAFC;
}

.dateRangeSelect:focus {
  background-color: #EBF4FF;
  color: var(--primary-color);
}

/* Loading State */
.loadingState {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
}

.loadingSpinner {
  width: 48px;
  height: 48px;
  border: 4px solid #E2E8F0;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loadingState p {
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
}

/* Metrics Grid */
.metricsGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.metricCard {
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #E2E8F0;
  transition: all 0.2s ease;
}

.metricCard:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.metricTitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0 0 12px 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metricValue {
  font-size: 36px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

/* Charts Grid */
.chartGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.chartCard {
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #E2E8F0;
}

.chartTitle {
  font-size: 18px;
  color: var(--text-primary);
  margin: 0 0 20px 0;
  font-weight: 600;
}

.chartContainer {
  height: 300px;
  width: 100%;
}

/* Table Card */
.tableCard {
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #E2E8F0;
  margin-bottom: 30px;
}

.dataTable {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
}

.dataTable th, 
.dataTable td {
  padding: 16px 12px;
  text-align: left;
  border-bottom: 1px solid #EDF2F7;
}

.dataTable th {
  background-color: #F7FAFC;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dataTable td {
  color: var(--text-primary);
  font-size: 14px;
}

.dataTable tr:hover {
  background-color: #F7FAFC;
}

.dataTable tr:last-child td {
  border-bottom: none;
}

.noDataMessage {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
  font-style: italic;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .chartGrid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .content {
    padding: 0 16px 40px;
  }
  
  .pageHeader {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
    padding-top: 20px;
  }
  
  .pageHeader h1 {
    font-size: 24px;
    text-align: center;
  }
  
  .filterSection {
    justify-content: center;
  }
  
  .metricsGrid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .chartGrid {
    gap: 16px;
  }
  
  .chartCard {
    padding: 16px;
  }
  
  .chartContainer {
    height: 250px;
  }
  
  .tableCard {
    padding: 16px;
    overflow-x: auto;
  }
  
  .dataTable {
    min-width: 500px;
  }
  
  .dataTable th,
  .dataTable td {
    padding: 12px 8px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .pageHeader h1 {
    font-size: 20px;
  }
  
  .metricValue {
    font-size: 28px;
  }
  
  .metricTitle {
    font-size: 14px;
  }
  
  .chartTitle {
    font-size: 16px;
  }
}